import React from "react";
import get from "lodash/get";
import TimeAgo from "react-timeago";
import { LazyLoadImages, ResponsiveHeroImage, ReviewRating } from "@quintype/components";
import { CustomLink } from "../custom-link";
import { ImageFallback } from "./image-fallback.js";
import { Author } from "./author.js";
import { altTagOfHeroImages, isExternalStory, getScrollSpeed, formatter } from "../../utils";
import PropTypes from "prop-types";
import { SvgHandler } from "../svg-handler/svg-handler";
import { GlideCarousel } from "../../components/atoms/glide";
import dotIcon from "../../../assets/icons/dot.svg";

function Carousel(props) {
  const { eager = false } = props;

  let resizeDone = false;

  const resize = () => {
    if (resizeDone === false) {
      global.dispatchEvent(new global.Event("resize"));
      resizeDone = true;
    }
  };
  const { associatedMetadata } = props;
  const paginationValue = get(associatedMetadata, ["enable pagination - dot or dash"], "dot").toLowerCase();

  const options = {
    numberOfStoriesToShow: props.numberOfStoriesToShow,
    afterSlide: resize,
    perView: 1,
    arrow: props.arrow,
    items: props.stories,
    dot: paginationValue,
    autoplay: getScrollSpeed(associatedMetadata)
  };
  const SliderWrapper = (
    <GlideCarousel {...options} className="carousel">
      {props.stories.slice(0, props.numberOfStoriesToShow).map((storyObj, index) => {
        const story = storyObj && storyObj.story ? storyObj.story : storyObj;
        const initialVisibleSlidesEager = index === 0 ? eager : false;
        return (
          <div key={story.id}>
            {React.createElement(props.itemTemplate || CarouselItem, {
              story: story,
              config: props.config,
              associatedMetadata: props.associatedMetadata,
              collection: props.collection,
              eager: initialVisibleSlidesEager
            })}
          </div>
        );
      })}
    </GlideCarousel>
  );

  if (!props.stories) return null;

  return (
    <div className="carousel-wrapper">
      <div
        className={`carousel slider ${
          get(props.config, ["theme-attributes", "language_direction"]) === "right" ? "dots-rtl" : ""
        }`}
      >
        {SliderWrapper}
      </div>
    </div>
  );
}

Carousel.propTypes = {
  associatedMetadata: PropTypes.object,
  stories: PropTypes.array,
  arrows: PropTypes.bool,
  config: PropTypes.shape({
    "theme-attributes": PropTypes.object,
    language_direction: PropTypes.string
  }),
  collection: PropTypes.object,
  numberOfStoriesToShow: PropTypes.number,
  itemTemplate: PropTypes.func,
  autoplay_duration_sec: PropTypes.number,
  eager: PropTypes.bool,
  arrow: PropTypes.bool
};

function CarouselItem(props) {
  const { story, collection, config = {}, associatedMetadata = {}, eager = false } = props;

  if (!story) return null;
  return (
    <CustomLink href={"" + story.url} externalLink={isExternalStory(story)}>
      <Slide
        eager={eager}
        associatedMetadata={associatedMetadata}
        story={story}
        config={config}
        collection={collection}
      />
    </CustomLink>
  );
}
CarouselItem.propTypes = {
  story: PropTypes.object,
  collection: PropTypes.object,
  config: PropTypes.object,
  associatedMetadata: PropTypes.object,
  eager: PropTypes.bool
};
function authorImageUrl(author) {
  if (!author || !author["avatar-url"]) return;

  // FIXME: Hack. Better to use FocusImage class from Quintype-JS
  if (author["avatar-s3-key"] && author["avatar-s3-key"] !== "") {
    return `${author["avatar-url"]}?w=96&h=96`;
  }

  return author["avatar-url"];
}

function Slide(props) {
  const {
    associatedMetadata: {
      show_section_tag: showSectionTag = true,
      show_author_name: showAuthorName = false,
      show_time_of_publish: showTimeOfPublish = true
    },
    story: { "story-template": storyTemplate },
    eager = false
  } = props;
  const isReviewStory = props.story["story-template"] === "review";
  const publishedTime = props.story["last-published-at"]
    ? props.story["last-published-at"]
    : props.story["first-published-at"];

  const imageWidth = get(props, ["config", "imageWidth"], []);

  return (
    <div>
      <div
        className={`carousel-slide ${
          get(props.config, ["theme-attributes", "language_direction"]) === "right" ? "rtl-section-header" : ""
        }`}
      >
        <div className="carousel-slide__image">
          {props.story["hero-image-s3-key"] ? (
            <figure className={`carousel-slide__image__${storyTemplate}`}>
              <LazyLoadImages margin={"0px"}>
                <ResponsiveHeroImage
                  story={props.story}
                  aspectRatio={[4, 3]}
                  alt={altTagOfHeroImages(props.story)}
                  defaultWidth={480}
                  widths={imageWidth}
                  sizes="(max-width: 420px) 80%, (max-width: 768px) 48%, 23%"
                  imgParams={{ auto: ["format", "compress"], fit: "max" }}
                  eager={eager}
                />
              </LazyLoadImages>
            </figure>
          ) : (
            <ImageFallback story={props.story} />
          )}
        </div>
        <div className="carousel-slide__content">
          {showSectionTag ? (
            <div className="carousel-slide__section" data-testid="carousel-sectionname">
              {get(props.story, ["sections", 0, "display-name"]) || get(props.story, ["sections", 0, "name"], "")}
            </div>
          ) : null}
          <h2 className="carousel-slide__heading" data-test-id="carousel-story-headline">
            <bdi dangerouslySetInnerHTML={{ __html: props.story.headline }} />
          </h2>
          {isReviewStory && <ReviewRating value={get(props.story, ["metadata", "review-rating", "value"], "0")} />}
          <p className="carousel-slide__description">
            <bdi dangerouslySetInnerHTML={{ __html: props.story.subheadline }} />
          </p>
          <div className="post-details">
            {showAuthorName ? (
              <div data-testid="carousel-authorname">
                <Author
                  author={Object.assign({}, get(props.story, ["authors", 0]), {
                    name: get(props.story, ["authors", 0, "name"], props.story["author-name"]),
                    image: authorImageUrl(get(props.story, ["authors", 0])),
                    id: get(props.story, ["authors", 0, "id"], props.story["author-id"])
                  })}
                  config={props.config}
                />
              </div>
            ) : null}
            {showAuthorName && showTimeOfPublish ? (
              <SvgHandler xlinkHref={dotIcon} clazzName="dot" width="3px" height="3px" />
            ) : null}
            {showTimeOfPublish ? (
              <div className="post-time" data-testid="carousel-showtime">
                <TimeAgo
                  date={publishedTime}
                  formatter={(value, unit, suffix, date) => formatter(value, unit, suffix, date)}
                  title={null}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

Slide.propTypes = {
  story: PropTypes.object,
  associatedMetadata: PropTypes.shape({
    show_section_tag: PropTypes.bool,
    show_author_name: PropTypes.bool,
    show_time_of_publish: PropTypes.bool
  }),
  config: PropTypes.object,
  eager: PropTypes.bool
};
export { Carousel };
